/**
 * Plan prices, in pence.
 *
 * ## Stripe is the source of truth. These are a cache of it.
 *
 * Decided 12 August 2026, extending the rule in CLAUDE.md that made Stripe
 * authoritative for plan *state* to cover plan *prices* too. If these constants
 * and Stripe ever disagree, **Stripe is right and this file is a bug** — never
 * the other way round, and never "fix" Stripe to match this.
 *
 * They exist here at all because `packages/core` does no I/O (rule 4) and the
 * pricing page must render without a network round trip. That makes them a
 * cache, and caches go stale silently — so **`pnpm check:pricing` reads the live
 * Stripe prices and fails if any of these drift.** Run it after any price
 * change, before shipping one.
 *
 * The values were originally ported from design/Clinic Stock v2.dc.html:4590 and
 * were verified against live Stripe on 12 August 2026.
 *
 * ⚠️ Do not take prices from `docs/design-reference/`. Those screenshots are
 * historical iterations and show at least two superseded schemes — a two-plan
 * £15/£25, and a three-plan £15/£25/£39. Neither is what anyone is charged.
 *
 * The authoritative *proration* figure is always Stripe's upcoming-invoice
 * preview, never arithmetic done here. The prototype's placeholder proration
 * math is deliberately not ported.
 */
export const PRICING = {
    starter: { monthly: 1800, yearlyTotal: 18_000, yearlyPerMonth: 1500 },
    pro: { monthly: 3200, yearlyTotal: 31_200, yearlyPerMonth: 2600 },
    premium: { monthly: 4500, yearlyTotal: 43_200, yearlyPerMonth: 3600 },
};
/**
 * 30 since 26 October 2026 (Emmanuel's decision on launch day). The prototype
 * and the first build used 14 (design/Clinic Stock v2.dc.html:3727); a clinic
 * needs a month of deliveries, use and a stock count before the app has shown
 * what it is worth. Applies to new sign-ups; a trial already running keeps
 * the end date it started with.
 */
export const TRIAL_DAYS = 30;
/** Annual saving vs paying monthly for twelve months. */
export function yearlySaving(plan) {
    const p = PRICING[plan];
    return p.monthly * 12 - p.yearlyTotal;
}
/** The amount a Stripe Price should charge for this plan + cycle. */
export function listPrice(plan, cycle) {
    return cycle === 'yearly' ? PRICING[plan].yearlyTotal : PRICING[plan].monthly;
}
/**
 * Maps a plan + cycle to the env var holding its Stripe Price ID. Six prices
 * total; created once per Stripe mode (test and live) — see docs/SETUP.md.
 */
export function stripePriceEnvKey(plan, cycle) {
    return `STRIPE_PRICE_${plan.toUpperCase()}_${cycle.toUpperCase()}`;
}
//# sourceMappingURL=pricing.js.map